import { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth-context';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Users, BookOpen, CalendarDays, Crown, UserPlus, MessageCircle, Target, TrendingUp, BarChart3, Video, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('this_month');

  useEffect(() => {
    Promise.all([
      api.get('/api/dashboard/stats'),
      api.get('/api/analytics/overview'),
    ]).then(([statsRes, analyticsRes]) => {
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
    }).finally(() => setLoading(false));
  }, []);

  const funnelData = analytics ? [
    { name: 'Free', value: analytics.funnel?.free || 0, color: '#94a3b8' },
    { name: 'Trial', value: analytics.funnel?.trial || 0, color: '#3b82f6' },
    { name: 'Elite', value: analytics.funnel?.gold || analytics.funnel?.elite || 0, color: '#f59e0b' },
  ] : [];

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome back, {user?.name}</p>
        </div>
        <Select value={dateFilter} onValueChange={setDateFilter}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="this_week">This Week</SelectItem>
            <SelectItem value="this_month">This Month</SelectItem>
            <SelectItem value="last_month">Last Month</SelectItem>
            <SelectItem value="all_time">All Time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <Card key={i} className="rounded-2xl animate-pulse"><CardContent className="pt-6 h-24" /></Card>)}
        </div>
      ) : (
        <>
          {/* Top KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <Card className="rounded-2xl hover:shadow-md transition-shadow duration-200"><CardContent className="pt-5 pb-4">
              <Users className="h-5 w-5 text-primary mb-2" />
              <p className="text-2xl font-semibold">{analytics?.users?.total || stats.total_learners || 0}</p>
              <p className="text-xs text-muted-foreground">Total Learners</p>
              {analytics?.users?.new_this_month > 0 && <p className="text-[10px] text-emerald-600 mt-0.5">+{analytics.users.new_this_month} this month</p>}
            </CardContent></Card>
            <Card className="rounded-2xl border-amber-200 hover:shadow-md transition-shadow duration-200"><CardContent className="pt-5 pb-4">
              <Crown className="h-5 w-5 text-amber-500 mb-2" />
              <p className="text-2xl font-semibold text-amber-900">{analytics?.users?.gold || stats.gold_learners || 0}</p>
              <p className="text-xs text-amber-700">Elite Members</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{analytics?.funnel?.conversion_trial_to_gold || 0}% conversion</p>
            </CardContent></Card>
            <Card className="rounded-2xl hover:shadow-md transition-shadow duration-200" data-testid="stat-card-sessions-completed"><CardContent className="pt-5 pb-4">
              <CalendarDays className="h-5 w-5 text-blue-600 mb-2" />
              <p className="text-2xl font-semibold">{analytics?.sessions?.completed || stats.completed_sessions || 0}</p>
              <p className="text-xs text-muted-foreground">Sessions Completed</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{analytics?.sessions?.upcoming || stats.upcoming_sessions || 0} upcoming</p>
            </CardContent></Card>
            <Card className={`rounded-2xl hover:shadow-md transition-shadow duration-200 ${stats.missed_sessions > 0 ? 'border-red-200 bg-red-50/50' : ''}`} data-testid="stat-card-missed-sessions"><CardContent className="pt-5 pb-4">
              <AlertTriangle className={`h-5 w-5 mb-2 ${stats.missed_sessions > 0 ? 'text-red-600' : 'text-muted-foreground'}`} />
              <p className={`text-2xl font-semibold ${stats.missed_sessions > 0 ? 'text-red-900' : ''}`}>{stats.missed_sessions || 0}</p>
              <p className={`text-xs ${stats.missed_sessions > 0 ? 'text-red-700' : 'text-muted-foreground'}`}>Missed Sessions</p>
              {stats.missed_sessions > 0 && <p className="text-[10px] text-red-600 mt-0.5 font-medium">Needs attention</p>}
            </CardContent></Card>
            <Card className="rounded-2xl hover:shadow-md transition-shadow duration-200"><CardContent className="pt-5 pb-4">
              <Target className="h-5 w-5 text-emerald-600 mb-2" />
              <p className="text-2xl font-semibold">{analytics?.sessions?.avg_attendance_pct || 0}%</p>
              <p className="text-xs text-muted-foreground">Avg Attendance</p>
            </CardContent></Card>
            <Card className="rounded-2xl hover:shadow-md transition-shadow duration-200"><CardContent className="pt-5 pb-4">
              <MessageCircle className="h-5 w-5 text-teal-600 mb-2" />
              <p className="text-2xl font-semibold">{analytics?.soulai?.active_users || 0}</p>
              <p className="text-xs text-muted-foreground">SoulAI Users</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{analytics?.soulai?.total_messages || 0} messages</p>
            </CardContent></Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="rounded-2xl">
              <CardHeader className="pb-2"><CardTitle className="font-heading text-lg flex items-center gap-2"><TrendingUp className="h-5 w-5" />User Growth</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={analytics?.monthly_signups || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="hsl(174, 44%, 28%)" radius={[4, 4, 0, 0]} name="New Learners" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="rounded-2xl">
              <CardHeader className="pb-2"><CardTitle className="font-heading text-lg flex items-center gap-2"><BarChart3 className="h-5 w-5" />Sessions Delivered</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={analytics?.monthly_sessions || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#e8945a" strokeWidth={2} dot={{ r: 4 }} name="Sessions" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Bottom Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Conversion Funnel */}
            <Card className="rounded-2xl">
              <CardHeader className="pb-2"><CardTitle className="font-heading text-lg">Conversion Funnel</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={funnelData} innerRadius={45} outerRadius={70} dataKey="value" nameKey="name" label={({ name, value }) => `${name}: ${value}`}>
                      {funnelData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-1">
                  {funnelData.map((f, i) => (
                    <div key={i} className="flex items-center gap-1 text-xs">
                      <div className="h-2 w-2 rounded-full" style={{ background: f.color }} />{f.name}: {f.value}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Platform Overview */}
            <Card className="rounded-2xl">
              <CardHeader className="pb-2"><CardTitle className="font-heading text-lg">Platform Overview</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {[
                  { label: 'Total Instructors', value: analytics?.instructors || stats.total_instructors || 0 },
                  { label: 'Active Batches', value: analytics?.batches?.total || stats.total_batches || 0 },
                  { label: '1:1 Batches', value: analytics?.batches?.one_on_one || stats.active_1on1 || 0 },
                  { label: 'Group Batches', value: analytics?.batches?.group || stats.active_group || 0 },
                  { label: 'Recorded Courses', value: analytics?.courses || 0 },
                  { label: 'Canceled Sessions', value: analytics?.sessions?.canceled || 0 },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/40 transition-colors">
                    <span className="text-sm text-muted-foreground">{item.label}</span>
                    <span className="text-sm font-semibold">{item.value}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="rounded-2xl">
              <CardHeader className="pb-2"><CardTitle className="font-heading text-lg">Quick Actions</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <a href="/admin/batches/create" className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-muted transition-colors duration-200">
                  <BookOpen className="h-4 w-4 text-primary" /><span className="text-sm">Create New Batch</span>
                </a>
                <a href="/admin/users" className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-muted transition-colors duration-200">
                  <Users className="h-4 w-4 text-primary" /><span className="text-sm">Manage Users</span>
                </a>
                <a href="/admin/calendar" className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-muted transition-colors duration-200">
                  <CalendarDays className="h-4 w-4 text-primary" /><span className="text-sm">View Calendar</span>
                </a>
                <a href="/admin/finance" className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-muted transition-colors duration-200">
                  <span className="text-sm">Review Payouts</span>
                </a>
                <a href="/admin/migration" className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-muted transition-colors duration-200">
                  <span className="text-sm">Sync from Wise.live</span>
                </a>
              </CardContent>
            </Card>
          </div>

          {/* Top Batches */}
          {analytics?.top_batches?.length > 0 && (
            <Card className="rounded-2xl">
              <CardHeader className="pb-2"><CardTitle className="font-heading text-lg">Top Batches by Learner Count</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                  {analytics.top_batches.map((b, i) => (
                    <div key={i} className="p-3 rounded-xl border text-center">
                      <p className="text-xs text-muted-foreground">#{i + 1}</p>
                      <p className="text-sm font-medium mt-1 truncate">{b.name}</p>
                      <Badge variant="secondary" className="mt-1">{b.learner_count} learners</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
